import {memo, useCallback, useMemo, useState} from 'react'
import {ActivityIndicator, ScrollView, View} from 'react-native'
import {AppBskyEmbedVideo, type AppBskyFeedDefs, moderatePost} from '@atproto/api'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {urls} from '#/lib/constants'
import {usePostViewTracking} from '#/lib/hooks/usePostViewTracking'
import {cleanError} from '#/lib/strings/errors'
import {augmentSearchQuery} from '#/lib/strings/helpers'
import {useActorSearch} from '#/state/queries/actor-search'
import {usePopularFeedsSearch} from '#/state/queries/feed'
import {useSearchPostsQuery} from '#/state/queries/search-posts'
import {useYouTubeSearchQuery, isYouTubeSearchEnabled} from '#/state/queries/youtube-search'
import {useStockSearchQuery, isStockSearchEnabled} from '#/state/queries/stock-search'
import {useStudioSearchVideosQuery} from '#/state/queries/studio'
import {useSession} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {useCloseAllActiveElements} from '#/state/util'
import {Pager} from '#/view/com/pager/Pager'
import {TabBar} from '#/view/com/pager/TabBar'
import {Post} from '#/view/com/post/Post'
import {ProfileCardWithFollowBtn} from '#/view/com/profile/ProfileCard'
import {List} from '#/view/com/util/List'
import {atoms as a, useBreakpoints, useTheme, web} from '#/alf'
import * as FeedCard from '#/components/FeedCard'
import * as Grid from '#/components/Grid'
import * as Layout from '#/components/Layout'
import {InlineLinkText} from '#/components/Link'
import {ListFooter} from '#/components/Lists'
import {SearchError} from '#/components/SearchError'
import {Text} from '#/components/Typography'
import {CompactVideoPostCard} from '#/components/VideoPostCard'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {StudioFeedVideoCard} from '#/screens/Studio/StudioFeedVideoCard'
import {YouTubeVideoCard} from './components/YouTubeVideoCard'
import {StockCard} from './components/StockCard'

let SearchResults = ({
  query,
  queryWithParams,
  activeTab,
  onPageSelected,
  headerHeight,
  initialPage = 0,
}: {
  query: string
  queryWithParams: string
  activeTab: number
  onPageSelected: (page: number) => void
  headerHeight: number
  initialPage?: number
}): React.ReactNode => {
  const {_} = useLingui()

  const sections = useMemo(() => {
    if (!queryWithParams) return []
    const noParams = queryWithParams === query
    return [
      {
        title: _(msg`Top`),
        component: (
          <SearchScreenPostResults
            query={queryWithParams}
            sort="top"
            active={activeTab === 0}
          />
        ),
      },
      {
        title: _(msg`Latest`),
        component: (
          <SearchScreenPostResults
            query={queryWithParams}
            sort="latest"
            active={activeTab === 1}
          />
        ),
      },
      noParams && {
        title: _(msg`People`),
        component: (
          <SearchScreenUserResults query={query} active={activeTab === 2} />
        ),
      },
      noParams && {
        title: _(msg`Feeds`),
        component: (
          <SearchScreenFeedsResults query={query} active={activeTab === 3} />
        ),
      },
      {
        title: _(msg`SHXRE Videos`),
        component: (
          <SearchScreenShxreVideoResults
            query={queryWithParams}
            active={activeTab === (noParams ? 4 : 2)}
          />
        ),
      },
      {
        title: _(msg`YouTube Videos`),
        component: (
          <SearchScreenYouTubeResults
            query={queryWithParams}
            active={activeTab === (noParams ? 5 : 3)}
          />
        ),
      },
      {
        title: _(msg`Stocks`),
        component: (
          <SearchScreenStockResults
            query={queryWithParams}
            active={activeTab === (noParams ? 6 : 4)}
          />
        ),
      },
    ].filter(Boolean) as {
      title: string
      component: React.ReactNode
    }[]
  }, [_, query, queryWithParams, activeTab])

  return (
    <Pager
      onPageSelected={onPageSelected}
      renderTabBar={props => (
        <Layout.Center style={[a.z_10, web([a.sticky, {top: headerHeight}])]}>
          <TabBar items={sections.map(section => section.title)} {...props} />
        </Layout.Center>
      )}
      initialPage={initialPage}>
      {sections.map((section, i) => (
        <View key={i}>{section.component}</View>
      ))}
    </Pager>
  )
}
SearchResults = memo(SearchResults)
export {SearchResults}

function Loader() {
  return (
    <Layout.Content>
      <View style={[a.py_xl]}>
        <ActivityIndicator />
      </View>
    </Layout.Content>
  )
}

function EmptyState({
  messageText,
  error,
  children,
}: {
  messageText: React.ReactNode
  error?: string
  children?: React.ReactNode
}) {
  const t = useTheme()

  return (
    <Layout.Content>
      <View style={[a.p_xl]}>
        <View style={[t.atoms.bg_contrast_25, a.rounded_sm, a.p_lg]}>
          <Text style={[a.text_md]}>{messageText}</Text>

          {error && (
            <>
              <View
                style={[
                  {
                    marginVertical: 12,
                    height: 1,
                    width: '100%',
                    backgroundColor: t.atoms.text.color,
                    opacity: 0.2,
                  },
                ]}
              />

              <Text style={[t.atoms.text_contrast_medium]}>
                <Trans>Error: {error}</Trans>
              </Text>
            </>
          )}

          {children}
        </View>
      </View>
    </Layout.Content>
  )
}

function NoResultsText({query}: {query: string}) {
  const t = useTheme()
  const {_} = useLingui()

  return (
    <>
      <Text style={[a.text_lg, t.atoms.text_contrast_high]}>
        <Trans>
          No results found for "
          <Text style={[a.text_lg, t.atoms.text, a.font_medium]}>{query}</Text>
          ".
        </Trans>
      </Text>
      {'\n\n'}
      <Text style={[a.text_md, a.leading_snug, t.atoms.text_contrast_high]}>
        <Trans context="english-only-resource">
          Try a different search term, or{' '}
          <InlineLinkText
            label={_(
              msg({
                message: 'read about how to use search filters',
                context: 'english-only-resource',
              }),
            )}
            to={urls.website.blog.searchTipsAndTricks}
            style={[a.text_md, a.leading_snug]}>
            read about how to use search filters
          </InlineLinkText>
          .
        </Trans>
      </Text>
    </>
  )
}

type SearchResultSlice =
  | {
      type: 'post'
      key: string
      post: AppBskyFeedDefs.PostView
    }
  | {
      type: 'loadingMore'
      key: string
    }

let SearchScreenPostResults = ({
  query,
  sort,
  active,
}: {
  query: string
  sort?: 'top' | 'latest'
  active: boolean
}): React.ReactNode => {
  const {_} = useLingui()
  const {currentAccount, hasSession} = useSession()
  const [isPTR, setIsPTR] = useState(false)
  const trackPostView = usePostViewTracking('SearchResults')

  const augmentedQuery = useMemo(() => {
    return augmentSearchQuery(query || '', {did: currentAccount?.did})
  }, [query, currentAccount])

  const {
    isFetched,
    data: results,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useSearchPostsQuery({query: augmentedQuery, sort, enabled: active})

  const t = useTheme()
  const onPullToRefresh = useCallback(async () => {
    setIsPTR(true)
    await refetch()
    setIsPTR(false)
  }, [setIsPTR, refetch])
  const onEndReached = useCallback(() => {
    if (isFetching || !hasNextPage || error) return
    fetchNextPage()
  }, [isFetching, error, hasNextPage, fetchNextPage])

  const posts = useMemo(() => {
    return results?.pages.flatMap(page => page.posts) || []
  }, [results])
  const items = useMemo(() => {
    let temp: SearchResultSlice[] = []

    const seenUris = new Set()
    for (const post of posts) {
      if (seenUris.has(post.uri)) {
        continue
      }
      temp.push({
        type: 'post',
        key: post.uri,
        post,
      })
      seenUris.add(post.uri)
    }

    if (isFetchingNextPage) {
      temp.push({
        type: 'loadingMore',
        key: 'loadingMore',
      })
    }

    return temp
  }, [posts, isFetchingNextPage])

  const closeAllActiveElements = useCloseAllActiveElements()
  const {requestSwitchToAccount} = useLoggedOutViewControls()

  const showSignIn = () => {
    closeAllActiveElements()
    requestSwitchToAccount({requestedAccount: 'none'})
  }

  const showCreateAccount = () => {
    closeAllActiveElements()
    requestSwitchToAccount({requestedAccount: 'new'})
  }

  if (!hasSession) {
    return (
      <SearchError
        title={_(msg`Search is currently unavailable when logged out`)}>
        <Text style={[a.text_md, a.text_center, a.leading_snug]}>
          <Trans>
            <InlineLinkText
              label={_(msg`Sign in`)}
              to={'#'}
              onPress={showSignIn}>
              Sign in
            </InlineLinkText>
            <Text style={t.atoms.text_contrast_medium}> or </Text>
            <InlineLinkText
              label={_(msg`Create an account`)}
              to={'#'}
              onPress={showCreateAccount}>
              create an account
            </InlineLinkText>
            <Text> </Text>
            <Text style={t.atoms.text_contrast_medium}>
              to search for news, sports, politics, and everything else
              happening on Bluesky.
            </Text>
          </Trans>
        </Text>
      </SearchError>
    )
  }

  return error ? (
    <EmptyState
      messageText={_(
        msg`We're sorry, but your search could not be completed. Please try again in a few minutes.`,
      )}
      error={cleanError(error)}
    />
  ) : (
    <>
      {isFetched ? (
        <>
          {posts.length ? (
            <List
              data={items}
              renderItem={({item}) => {
                if (item.type === 'post') {
                  return <Post post={item.post} />
                } else {
                  return null
                }
              }}
              keyExtractor={item => item.key}
              refreshing={isPTR}
              onRefresh={onPullToRefresh}
              onEndReached={onEndReached}
              onItemSeen={item => {
                if (item.type === 'post') {
                  trackPostView(item.post)
                }
              }}
              desktopFixedHeight
              ListFooterComponent={
                <ListFooter
                  isFetchingNextPage={isFetchingNextPage}
                  hasNextPage={hasNextPage}
                />
              }
            />
          ) : (
            <EmptyState messageText={<NoResultsText query={query} />} />
          )}
        </>
      ) : (
        <Loader />
      )}
    </>
  )
}
SearchScreenPostResults = memo(SearchScreenPostResults)

let SearchScreenUserResults = ({
  query,
  active,
}: {
  query: string
  active: boolean
}): React.ReactNode => {
  const {_} = useLingui()
  const {hasSession} = useSession()
  const [isPTR, setIsPTR] = useState(false)

  const {
    isFetched,
    data: results,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useActorSearch({
    query,
    enabled: active,
  })

  const onPullToRefresh = useCallback(async () => {
    setIsPTR(true)
    await refetch()
    setIsPTR(false)
  }, [setIsPTR, refetch])
  const onEndReached = useCallback(() => {
    if (!hasSession) return
    if (isFetching || !hasNextPage || error) return
    fetchNextPage()
  }, [isFetching, error, hasNextPage, fetchNextPage, hasSession])

  const profiles = useMemo(() => {
    return results?.pages.flatMap(page => page.actors) || []
  }, [results])

  if (error) {
    return (
      <EmptyState
        messageText={_(
          msg`We're sorry, but your search could not be completed. Please try again in a few minutes.`,
        )}
        error={error.toString()}
      />
    )
  }

  return isFetched && profiles ? (
    <>
      {profiles.length ? (
        <List
          data={profiles}
          renderItem={({item}) => <ProfileCardWithFollowBtn profile={item} />}
          keyExtractor={item => item.did}
          refreshing={isPTR}
          onRefresh={onPullToRefresh}
          onEndReached={onEndReached}
          desktopFixedHeight
          ListFooterComponent={
            <ListFooter
              hasNextPage={hasNextPage && hasSession}
              isFetchingNextPage={isFetchingNextPage}
            />
          }
        />
      ) : (
        <EmptyState messageText={<NoResultsText query={query} />} />
      )}
    </>
  ) : (
    <Loader />
  )
}
SearchScreenUserResults = memo(SearchScreenUserResults)

let SearchScreenFeedsResults = ({
  query,
  active,
}: {
  query: string
  active: boolean
}): React.ReactNode => {
  const t = useTheme()

  const {data: results, isFetched} = usePopularFeedsSearch({
    query,
    enabled: active,
  })

  return isFetched && results ? (
    <>
      {results.length ? (
        <List
          data={results}
          renderItem={({item}) => (
            <View
              style={[
                a.border_t,
                t.atoms.border_contrast_low,
                a.px_lg,
                a.py_lg,
              ]}>
              <FeedCard.Default view={item} />
            </View>
          )}
          keyExtractor={item => item.uri}
          desktopFixedHeight
          ListFooterComponent={<ListFooter />}
        />
      ) : (
        <EmptyState messageText={<NoResultsText query={query} />} />
      )}
    </>
  ) : (
    <Loader />
  )
}
SearchScreenFeedsResults = memo(SearchScreenFeedsResults)

let SearchScreenShxreVideoResults = ({
  query,
  active,
}: {
  query: string
  active: boolean
}): React.ReactNode => {
  const {_} = useLingui()
  const t = useTheme()
  const [isPTR, setIsPTR] = useState(false)

  const {
    data: videos = [],
    isLoading,
    isFetched,
    error,
    refetch,
  } = useStudioSearchVideosQuery(query, {enabled: active})

  const onPullToRefresh = useCallback(async () => {
    setIsPTR(true)
    await refetch()
    setIsPTR(false)
  }, [refetch])

  if (error) {
    return (
      <EmptyState
        messageText={_(
          msg`We're sorry, but your search could not be completed. Please try again in a few minutes.`,
        )}
        error={cleanError(error)}
      />
    )
  }

  if (!isFetched || isLoading) {
    return <Loader />
  }

  if (videos.length === 0) {
    return (
      <Layout.Content>
        <EmptyState
          messageText={
            query.trim() ? (
              <NoResultsText query={query} />
            ) : (
              <Trans>No SHXRE long-form videos yet. Try a search term to filter.</Trans>
            )
          }
        />
      </Layout.Content>
    )
  }

  return (
    <Layout.Content>
      <List
        data={videos}
        renderItem={({item}) => (
          <StudioFeedVideoCard key={item.id} video={item} />
        )}
        keyExtractor={item => item.id}
        refreshing={isPTR}
        onRefresh={onPullToRefresh}
      />
    </Layout.Content>
  )
}
SearchScreenShxreVideoResults = memo(SearchScreenShxreVideoResults)

let SearchScreenYouTubeResults = ({
  query,
  active,
}: {
  query: string
  active: boolean
}): React.ReactNode => {
  const {_} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const [isPTR, setIsPTR] = useState(false)

  const {
    isFetched,
    data: results,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useYouTubeSearchQuery({query: query.trim(), enabled: active})

  const items = useMemo(() => {
    return results?.pages.flatMap(page => page.items) ?? []
  }, [results])

  const onPullToRefresh = useCallback(async () => {
    setIsPTR(true)
    await refetch()
    setIsPTR(false)
  }, [refetch])

  const onEndReached = useCallback(() => {
    if (!hasNextPage) return
    fetchNextPage()
  }, [hasNextPage, fetchNextPage])

  if (!isYouTubeSearchEnabled()) {
    return (
      <Layout.Content>
        <View style={[a.p_xl]}>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            <Trans>
              YouTube search is not configured. Set EXPO_PUBLIC_YOUTUBE_API_KEY to
              enable it.
            </Trans>
          </Text>
        </View>
      </Layout.Content>
    )
  }

  if (error) {
    return (
      <Layout.Content>
        <EmptyState
          messageText={error.message}
        />
      </Layout.Content>
    )
  }

  if (!isFetched) {
    return <Loader />
  }

  if (items.length === 0) {
    return (
      <Layout.Content>
        <EmptyState messageText={<NoResultsText query={query} />} />
      </Layout.Content>
    )
  }

  const cols = gtMobile ? 3 : 2

  return (
    <Layout.Content>
      <List
        data={items}
        renderItem={({item}) => (
          <View style={[a.p_sm]}>
            <YouTubeVideoCard item={item} />
          </View>
        )}
        keyExtractor={item => item.id}
        numColumns={cols}
        refreshing={isPTR}
        onRefresh={onPullToRefresh}
        onEndReached={onEndReached}
        desktopFixedHeight
        ListFooterComponent={
          <ListFooter
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
          />
        }
      />
    </Layout.Content>
  )
}
SearchScreenYouTubeResults = memo(SearchScreenYouTubeResults)

let SearchScreenStockResults = ({
  query,
  active,
}: {
  query: string
  active: boolean
}): React.ReactNode => {
  const {_} = useLingui()
  const t = useTheme()

  const {
    isFetched,
    data: results,
    error,
    refetch,
  } = useStockSearchQuery({query: query.trim(), enabled: active})

  const [isPTR, setIsPTR] = useState(false)
  const onPullToRefresh = useCallback(async () => {
    setIsPTR(true)
    await refetch()
    setIsPTR(false)
  }, [refetch])

  if (!isStockSearchEnabled()) {
    return (
      <Layout.Content>
        <View style={[a.p_xl]}>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            <Trans>
              Stock search is not configured. Set EXPO_PUBLIC_FINNHUB_API_KEY to
              enable it.
            </Trans>
          </Text>
        </View>
      </Layout.Content>
    )
  }

  if (error) {
    return (
      <Layout.Content>
        <EmptyState messageText={error.message} />
      </Layout.Content>
    )
  }

  if (!isFetched) {
    return <Loader />
  }

  if (!results || results.length === 0) {
    return (
      <Layout.Content>
        <EmptyState
          messageText={
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              <Trans>
                No stock results for "{query}". Try a ticker symbol like AAPL,
                TSLA, or GOOGL.
              </Trans>
            </Text>
          }
        />
      </Layout.Content>
    )
  }

  return (
    <Layout.Content>
      <List
        data={results}
        renderItem={({item}) => (
          <View style={[a.px_md, a.py_xs]}>
            <StockCard item={item} />
          </View>
        )}
        keyExtractor={item => item.symbol}
        refreshing={isPTR}
        onRefresh={onPullToRefresh}
        desktopFixedHeight
        ListFooterComponent={<ListFooter />}
      />
    </Layout.Content>
  )
}
SearchScreenStockResults = memo(SearchScreenStockResults)
